import React, {Fragment, useEffect, useState} from 'react';
import Button from '@material-ui/core/Button';
import {useTranslation} from "react-i18next";
import {useForm} from "react-hook-form";
import {Backdrop, CircularProgress, Grid, MenuItem} from "@material-ui/core";
import {SelectValidator, ValidatorForm} from 'react-material-ui-form-validator';
import {useDispatch, useSelector} from "react-redux";
import InputAtm from "../../../../../atomic/Input/Input";
import ColAtm from "../../../../../atomic/Col/Col";
import ButtonAtm from "../../../../../atomic/Button/Button";
import Editor from 'react-simple-wysiwyg';
import {getProperty, isDefined} from "../../../../../../utils";
import {handleEditorPaste} from "../../../../../../utils/sanitizeHtml";
import {
    createBlog,
    createBlogClearState, getBlogList,
    uploadBlogImage,
    uploadBlogImageClearState, getCategoryBlogList
} from "../../../../../../redux/actions/Web/Blog/blogActions";
import {getLanguages} from "../../../../../../redux/actions/languageActions";
import Alert from "@material-ui/lab/Alert";
import {PageTitle} from "../../../../../atomic/PageHeader/PageTitle";
import PageHeaderAtm from "../../../../../atomic/PageHeader/PageHeader";
import {WEB_BLOG_LIST} from "../../../../../../constants/appUrls.constants";
import {Animated} from "react-animated-css";
import Container from "@material-ui/core/Container";
import {makeStyles} from "@material-ui/core/styles";
import {history} from "../../../../../../utils/history";
import {openAI, clearOpenAI} from '../../../../../../redux/actions/General/OpenAI/openAIActions';

const useStyles = makeStyles((theme) => ({
    backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        color: '#d71f26',
    },
}))

export default function CreateBlogModal() {
    const classes = useStyles();
    const {t} = useTranslation();
    const {handleSubmit} = useForm();

    const createImage = useSelector(props => props.blogReducers?.createImage);
    const blogData = useSelector(props => props.blogReducers?.createBlog);
    const createBlogError = useSelector(props => props.blogReducers?.createBlogError);
    const languageList = useSelector(props => props.languagesReducer?.languages);
    const createImageError = useSelector(props => props.blogReducers?.createImageError);
    const createImageFetching = useSelector(props => props.blogReducers?.createImageFetching);
    const contentTypes = useSelector(state => state.blogReducers?.categoryBlogList);
    const openAIResult = useSelector(props => props?.openAIReducer?.listData);
    const propertyProducts = useSelector(props => props?.propertyProductsReducer);

    const webProduct = !propertyProducts?.fetching && propertyProducts?.get_products?.data
        ? Object.values(propertyProducts.get_products.data).find(item => item.route === "web")
        : null;
    const aiContentEnabled = webProduct?.parameters?.length === 0 ? true
        : webProduct?.parameters?.some(p => p?.product_parameter_id === 402 && p?.is_selected === 1) ?? false;

    const dispatch = useDispatch();

    const [html, setHtml] = useState("");
    const [filePath, setFilePath] = useState("");
    const [file, setFile] = useState("");
    const [title, setTitle] = useState("");
    const [language, setLanguage] = useState("");
    const [contentType, setContentType] = useState("");
    const [aiLoading, setAiLoading] = useState(false);

    useEffect(() => {
        dispatch(getLanguages());
    }, [dispatch]);
    
    useEffect(() => {
        dispatch(getCategoryBlogList());
    }, [dispatch]);

    useEffect(() => {
        if (blogData.status === 200) {
            dispatch(getBlogList());
            dispatch(createBlogClearState());
            dispatch(uploadBlogImageClearState());
            history.push(WEB_BLOG_LIST)
        }
    }, [blogData]);

    useEffect(() => {
        setHtml("")
        setFilePath("")
        setFile("")
        setTitle("")
        setLanguage("")
        setContentType("")
        dispatch(createBlogClearState());
        dispatch(uploadBlogImageClearState());
    }, [dispatch]);

    useEffect(() => {
        if (createImageError) {
            setFilePath("")
            setFile("")
        }
    }, [createImageError]);

    useEffect(() => {
        if (openAIResult && typeof openAIResult === "object" && Object.keys(openAIResult).length > 0) {
            setHtml(openAIResult?.content);
            setAiLoading(false);
            dispatch(clearOpenAI());
        }
    }, [openAIResult]);

    const handleChangeTitle = ({target}) => {
        setTitle(target.value);
    };

    const selectLang = ({target}) => {
        setLanguage(target.value)
    };

    const onSubmit = (data, e) => {
        e.preventDefault();

        const params = {
            property_id: getProperty(),
            title: title,
            language_code: language,
            content: html,
            image: createImage?.image ? createImage.image : null,
            content_category_id: contentType || 1
        }

        dispatch(createBlog(params));
    };

    function onChange(e) {
        setHtml(e.target.value);
    }

    const selectContentType = ({target}) => {
        setContentType(target.value)
    };

    const onFileChange = event => {
        event.preventDefault();
        const file = event.target.files[0];
        if (isDefined(file)) {
            setFilePath(URL.createObjectURL(file))
            setFile(event.target.files[0])
        }
        const params = {
            property_id: getProperty(),
            file: file,
        }
        dispatch(uploadBlogImage(params));
    };

    const handleAIContent = () => {
        setAiLoading(true);
        let aiMessage = [
            {
                role: "system",
                content: "Sen bir SEO uzmanısın. Cevaplarını SADECE JSON formatında ver. Object key'inin ismi content olsun. Girilen bilgilere göre açıklayıcı ve SEO uyumlu bir içerik üret"
            },
            {
                role: "user",
                content: `Başlığım ${title}, içerik türüm ${contentType} ve dil ${language || 'Türkçe'}. Bunlara göre bana seo uyumlu bir içerik hazırla. Maksimum 3000 karakter olsun. Html tagları ekleyebilirsin ama link ekleme.`
            }
        ];  
        dispatch(openAI({ message: aiMessage }));
    };

    return (
        <div className="mb-64">
            <PageTitle title={t('enw-create_content')}/>
            <PageHeaderAtm backButton={WEB_BLOG_LIST} exitText={t('btn-content-title')} text={t('enw-create_content')}/>
            <Animated animationIn="bounceInRight" animationInDuration={1000} isVisible={true}>
                <Container maxWidth="xl" className='blog-container'>
                    <div className="container-width">
                        <Fragment>
                            <ValidatorForm onSubmit={handleSubmit(onSubmit)}>
                                <Grid container className="blog-edit-and-create" spacing={3} md={8}>
                                    {blogData?.status === 200 &&
                                        <Alert className="w-100" severity="success">{t('enw-redirect_to_save-message')}</Alert>}
                                    {createBlogError &&
                                        <Alert className="w-100" severity="error">{createBlogError?.response?.data?.message}</Alert>}
                                    {createImageError &&
                                        <Alert className="w-100" severity="error">{createImageError?.response?.data?.message}</Alert>}
                                    {
                                        createImageFetching &&
                                        <Backdrop className={classes.backdrop} open>
                                            <CircularProgress color="inherit" />
                                        </Backdrop>
                                    }
                                    <Grid item xs={12} md={12}>
                                        <InputAtm
                                            label={t('enw-web_blog-title')}
                                            placeholder={t('enw-web_blog-title')}
                                            variant="outlined"
                                            autoComplete="off"
                                            onChange={handleChangeTitle}
                                            validators={['required']}
                                            errorMessages={[t("enw-notification_this_is_required")]}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6} className="blog-margin">
                                        <SelectValidator
                                            name="contentType"
                                            onChange={selectContentType}
                                            value={contentType}
                                            className="form-control"
                                            variant="outlined"
                                            fullWidth
                                            label={t('enw-content_type')}
                                            validators={['required']}
                                            errorMessages={[t("enw-notification_this_is_required")]}
                                        >
                                            {contentTypes.map(type => (
                                                <MenuItem key={type.id} value={type.id}>
                                                    {t(type.language_key)}
                                                </MenuItem>
                                            ))}
                                        </SelectValidator>
                                    </Grid>
                                    <Grid item xs={12} md={aiContentEnabled ? 3 : 6}>
                                        <SelectValidator
                                            name="category"
                                            onChange={selectLang}
                                            value={language}
                                            className="form-control"
                                            variant="outlined"
                                            fullWidth
                                            label={t('enw-input-language')}
                                            validators={['required']}
                                            errorMessages={[t("enw-notification_this_is_required")]}
                                        >
                                            {
                                                languageList?.map((lang) => (
                                                    <MenuItem value={lang.code}>
                                                        <img src={`/assets/img/language/${lang.code}.svg`} alt="en"
                                                             style={{width: 20, marginRight: 8}}/> {t(lang.language_key)}
                                                    </MenuItem>
                                                ))
                                            }
                                        </SelectValidator>
                                    </Grid>
                                    {/* AI BUTTON */}
                                    {aiContentEnabled &&
                                        <Grid item xs={12} md={3}>
                                            <ButtonAtm
                                                type="button"
                                                onClick={handleAIContent}
                                                className="blog-ai-button"
                                                startIcon={
                                                    aiLoading ?
                                                    <i style={{fontSize: "16px", marginRight: "6px"}} className="fas fa-spinner-third fa-spin"></i>
                                                    :
                                                    <svg xmlns="http://www.w3.org/2000/svg" style={{color: "#d71f26", marginRight: "6px"}} className="competitor-ai-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"></path>
                                                        <path d="M20 3v4"></path>
                                                        <path d="M22 5h-4"></path>
                                                        <path d="M4 17v2"></path>
                                                        <path d="M5 18H3"></path>
                                                    </svg>
                                                }
                                                buttontext={t('enw-create_with_ai-title')}
                                                disabled={aiLoading || !isDefined(title) || !isDefined(contentType) || !isDefined(language)}
                                            >
                                            </ButtonAtm>
                                        </Grid>
                                    }
                                    {/* onPaste sarmalayıcıda: paste olayı editörden yukarı kabarır,
                                        böylece kütüphanenin prop iletimine bağlı kalmayız */}
                                    <Grid item xs={12} className="blog-margin" onPaste={handleEditorPaste}>
                                        <Editor value={html} onChange={onChange}/>
                                    </Grid>
                                    <Grid item xs={12} className="mb-24 d-flex">
                                        <Grid item xs={12} md={4} className="blog-img-area">
                                            <input
                                                className="d-none"
                                                accept="image/png, image/jpeg, .pdf"
                                                id="contained-button-file"
                                                multiple
                                                type="file"
                                                onChange={onFileChange}
                                            />
                                            <label htmlFor="contained-button-file">
                                                <Button
                                                    className="room-rate-add soft-blue-button m-auto radius-8"
                                                    component="span"
                                                    startIcon={<i className="fas fa-cloud-upload-alt"/>}>
                                                    {t('btn-upload_image')}
                                                </Button>
                                            </label>
                                        </Grid>
                                        <div>
                                            {
                                                !isDefined(file) ?
                                                    <label htmlFor="contained-button-file"
                                                           className="cursor-pointer"></label>
                                                    :
                                                    <div className="blog-preview-img">
                                                        <img src={filePath}/>
                                                    </div>
                                            }
                                        </div>
                                    </Grid>
                                </Grid>
                                <ColAtm className="mt-32" md={12}>
                                    <ButtonAtm
                                        className="m-auto room-rate-modal-save bg-red"
                                        buttontext={t('btn-save')}
                                        color="primary"
                                        type="submit"
                                        disabled={!isDefined(html)}
                                    />
                                </ColAtm>
                            </ValidatorForm>
                        </Fragment>
                    </div>
                </Container>
            </Animated>
        </div>
    );
}
